import { FC, useCallback, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useKpiData } from "../hooks/UseKpiData";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { DashboardExportButton, DashboardNarrativePanel } from "src/components/common";
import { KPIDashboard, TabsDashboard } from "../components";
import { useDashboardNarrative } from "src/hooks/UseDashboardNarrative";
import type { TabId } from "../types/Tabs";
import MonthRangePicker from "src/components/reusable/MonthRangePicker/MonthRangePicker";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";

/**
 * Default: previous month vs current month.
 *
 * The toolbar picker runs in `lockEndToNextMonth` mode, whose invariant is
 * `start = end - 1 month`. A day-based range (e.g. last 30 days) lands both
 * ends inside the same month, so the picker — which snaps each value to the
 * start of its month — rendered "Jul, 2026 vs Jul, 2026" until the first month
 * change re-derived the start.
 */
const getDefaultMonthRange = () => {
  const now = dayjs();
  return {
    start: now.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
    end: now.startOf("month").format("YYYY-MM-DD"),
  };
};

const WebAnalyticsDashboard: FC = () => {
  const { t, i18n } = useTranslation();
  const apiLang = getApiLangFromI18n(i18n);
  const defaultRange = getDefaultMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [activeTabNarrativeData, setActiveTabNarrativeData] = useState<{
    tabKey: TabId;
    rawData: unknown;
    loading: boolean;
    error: string | null;
  }>({
    tabKey: "users",
    rawData: null,
    loading: true,
    error: null,
  });
  const { kpiData, rawData: kpiRawData, loading, error } = useKpiData(startDate, endDate, apiLang);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const narrativePayloadData = useMemo(
    () => ({
      dashboardKpi: kpiRawData,
      activeTabData: activeTabNarrativeData.rawData,
    }),
    [activeTabNarrativeData.rawData, kpiRawData],
  );
  const narrativeEnabled = useMemo(
    () =>
      !loading &&
      !activeTabNarrativeData.loading &&
      !error &&
      !activeTabNarrativeData.error &&
      Boolean(kpiRawData) &&
      Boolean(activeTabNarrativeData.rawData),
    [
      activeTabNarrativeData.error,
      activeTabNarrativeData.loading,
      activeTabNarrativeData.rawData,
      error,
      kpiRawData,
      loading,
    ],
  );
  const handleTabNarrativeDataChange = useCallback(
    (nextData: {
      tabKey: TabId;
      rawData: unknown;
      loading: boolean;
      error: string | null;
    }) => {
      setActiveTabNarrativeData(nextData);
    },
    [],
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "web_analytics",
    tabKey: activeTabNarrativeData.tabKey,
    startDate,
    endDate,
    data: narrativePayloadData,
    enabled: narrativeEnabled,
  });

  const handleRangeChange = ({ start, end }: { start: string; end: string }) => {
    setStartDate(start);
    setEndDate(end);
  };

  const showUnifiedNoData = !loading && (!!error || !kpiData.length);

  return (
    <div className="web-analytics-dashboard-with-data" ref={pdfContainerRef}>
      <div className="web-analytics-dashboard-with-data__toolbar dashboard-filter-toolbar" data-pdf-block="dashboard-heading">
        <div className="web-analytics-dashboard-with-data__heading">
          <h2 className="web-analytics-dashboard-with-data__title">
            {t("webAnalytics.title")}
          </h2>
          <p className="web-analytics-dashboard-with-data__subtitle">
            {t("webAnalytics.subtitle")}
          </p>
        </div>
        <div className="web-analytics-dashboard-with-data__date-range">
          <MonthRangePicker
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleRangeChange}
            lockEndToNextMonth
          />
          <DashboardExportButton
            dashboardName="Web-Analytics"
            dateRange={{ startDate, endDate }}
            containerRef={pdfContainerRef}
            disabled={loading}
          />
        </div>
      </div>
      <div className="web-analytics-dashboard-with-data__content">
        <div className="web-analytics-dashboard-with-data__narrative" data-pdf-block="narrative">
          <DashboardNarrativePanel
            narrative={narrative}
            loading={loading || activeTabNarrativeData.loading || narrativeLoading}
            aiSummary={aiSummary}
          />
        </div>
        {loading && (
          <div className="web-analytics-dashboard-with-data__unified-loading">
            <LoadingSpinner compact />
          </div>
        )}
        {showUnifiedNoData && (
          <div className="web-analytics-dashboard-with-data__no-data">
            {t("webAnalytics.noData")}
          </div>
        )}
        {!showUnifiedNoData && (
          <>
            <KPIDashboard kpiData={kpiData} loading={loading} suppressSpinner={loading} />
            <TabsDashboard
              startDate={startDate}
              endDate={endDate}
              suppressSpinner={loading}
              locale={apiLang}
              onNarrativeDataChange={handleTabNarrativeDataChange}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default WebAnalyticsDashboard;
